import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useEffect, useMemo, useRef } from "react"

import PaymentTermTable from "../components/PaymentTermTable"
import type { PaymentTermSortableAccessorKey } from "../constants/paymentTermSorting"
import { accessorKeyToPaymentTermSortField, paymentTermSortFieldToAccessoryKey } from "../constants/paymentTermSorting"
import type { PaymentTermSearchFilters } from "../stores/usePaymentTermSearchStore"
import { usePaymentTermSearchStore } from "../stores/usePaymentTermSearchStore"

import { Button } from "@/components/common/Button"
import type { PaymentTermSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"

const PaymentTermsContainer: FC = () => {
  const navigate = useNavigate()
  const { field, order } = useSearch({ from: "/app/admin/accounting/_layout/payment-terms" })

  const data = usePaymentTermSearchStore(({ data }) => data)
  const fetching = usePaymentTermSearchStore(({ fetching }) => fetching)
  const error = usePaymentTermSearchStore(({ error }) => error)
  const executeSearch = usePaymentTermSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = usePaymentTermSearchStore(({ loadMore }) => loadMore)
  const clearStore = usePaymentTermSearchStore(({ clearStore }) => clearStore)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = paymentTermSortFieldToAccessoryKey.get(field)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    const searchFilters: PaymentTermSearchFilters = {
      field,
      order,
    }
    executeSearch(queryClient, searchFilters)
  }, [field, order, executeSearch])

  const [throttledLoadMore] = useThrottle(() => {
    const searchFilters: PaymentTermSearchFilters = {
      field,
      order,
    }
    loadMore(queryClient, searchFilters)
  }, 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const handleSortingChange: OnChangeFn<SortingState> = (updaterFn) => {
    // @ts-expect-error - updaterFn is know fn
    const newSorting = updaterFn(sorting)
    if (newSorting.length) {
      const sort = newSorting[0]
      const key = sort.id as PaymentTermSortableAccessorKey
      const field = accessorKeyToPaymentTermSortField.get(key) as PaymentTermSortField
      const params = {
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      navigate({
        search: params,
        to: "/app/admin/accounting/payment-terms",
        replace: true,
      })
    }
  }

  const handleClickNewPaymentTerm = () => {
    navigate({
      to: "/app/admin/accounting/create-payment-term",
      replace: true,
    })
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <Box className='mb-4 flex justify-end'>
        <Button startIcon={<AddOutlined />} appearance='outlined' onClick={handleClickNewPaymentTerm}>
          New Payment Terms
        </Button>
      </Box>

      <Box className='flex h-full grow flex-col overflow-hidden'>
        <PaymentTermTable
          data={data}
          scrollHandler={handleScroll}
          tableContainerRef={tableContainerRef}
          onSortingChange={handleSortingChange}
          sorting={sorting}
          fetching={fetching}
          error={error}
        />
      </Box>
    </Box>
  )
}

export default PaymentTermsContainer
